import React from 'react';
import * as PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from '../../connect';
import { amplitudeLogEvent } from '../../helpers/amplitude-helper';
import { CompactItemList } from '../../components/compact-item-list';
import { LoadingIndicator } from '../../components/loading-indicator';
import { NotFound } from '../../components/not-found';
import logger from '../../utils/logger';
import { getMembershipLabel } from '../../utils/membership';
import { getGroup, createJoinGroupMembership, createFollowGroupMembership } from '../../actions';
import './index.less';
import { IAuthState } from '../../reducers/auth-reducers';
import { ISubscriptionState } from '../../reducers/subscription-reducers';
import { IGroupMembershipState, IGroupState } from '../../reducers/group-membership-reducers';
import { IReduxProps } from '../../actions/action-types';
import { createOpenGraphMetadata } from '../../helpers/metadata-helper';
import baseUrls from '../../data/baseUrls';

function mapStateToProps(
	{ auth, group, groupMembership, subscription },
	{
		match: {
			params: { groupToken },
		},
	}
) {
	return { auth, group, groupToken, groupMembership, subscription };
}

interface IGroupPageProps extends IReduxProps {
	auth: IAuthState;
	group: IGroupState;
	groupToken: string;
	groupMembership: IGroupMembershipState;
	subscription: ISubscriptionState;
}

@connect(mapStateToProps)
export default class GroupPage extends React.Component<IGroupPageProps> {
	static contextTypes = {
		router: PropTypes.object.isRequired,
	};

	static async activate(store, { params: { groupToken }, staticContext }) {
		await store.dispatch(getGroup(groupToken)).catch(logger.error);

		if (staticContext) {
			const {
				group: { error },
			} = store.getState();
			const statusCode = error?.statusCode;
			if (statusCode === 404) {
				staticContext.statusCode = 404;
			} else if (statusCode) {
				staticContext.statusCode = 500;
			}
		}
	}

	componentDidMount() {
		amplitudeLogEvent('Group - Viewed', { Token: this.props.groupToken });
	}

	componentWillReceiveProps(nextProps: IGroupPageProps) {
		const { groupMembership, dispatch, groupToken } = this.props;
		const { groupMembership: nextGroupMembership } = nextProps;
		if (
			!nextGroupMembership.isProcessing &&
			nextGroupMembership.created !== groupMembership.created &&
			nextGroupMembership.created
		) {
			dispatch(getGroup(groupToken, { reload: true })).catch(logger.error);
		}
	}

	_createGroupMembership(groupId: number, action: string) {
		const {
			auth,
			dispatch,
			group: { group },
		} = this.props;
		const isAuthenticated = !auth.isProcessing && auth.user && auth.user.id !== '-1';

		if (!isAuthenticated) {
			const returnUrl = group && group.token ? `/groups/${group.token}` : '/';
			this.context.router.history.push(
				`/register?returnUrl=${encodeURIComponent(returnUrl)}&groupMembershipId=${encodeURIComponent(
					groupId.toString()
				)}&groupMembershipAction=${encodeURIComponent(action)}`
			);
		} else if (action === 'join') {
			dispatch(createJoinGroupMembership(groupId)).catch(logger.error);
		} else if (action === 'follow') {
			dispatch(createFollowGroupMembership(groupId)).catch(logger.error);
		}
	}

	render() {
		const {
			auth,
			groupToken,
			subscription,
			group: { isFetching, group, groupMembership },
		} = this.props;
		const groupInfoParts: string[] = [];
		let groupMembershipAction;
		let showCreateGroupMembership;
		let membershipKind;
		let purchaseInfo;

		if (group) {
			groupInfoParts.push(group.kindDisplayName);

			if (group.city) {
				groupInfoParts.push(group.city + (group.stateAbbrev ? ', ' + group.stateAbbrev : ''));
			}

			if (group.memberCount) {
				groupInfoParts.push(`${group.memberCount.toLocaleString()} members`);
			}

			if (group.followerCount) {
				groupInfoParts.push(`${group.followerCount.toLocaleString()} followers`);
			}

			showCreateGroupMembership = group.availableActions.relationship === 'none';
			groupMembershipAction =
				group.availableActions.signInAndJoin || group.availableActions.join
					? 'join'
					: group.availableActions.signInAndFollow || group.availableActions.follow
					? 'follow'
					: null;
			membershipKind = group.relationship && group.relationship.membership && group.relationship.membership.kind;

			const membershipKindText = getMembershipLabel(membershipKind);
			if (membershipKindText) {
				groupInfoParts.push(membershipKindText);
			}
			purchaseInfo = group && group.productInfo && (group.productInfo.quickBuy || group.productInfo.addToCart);
		}

		const hasGroupMembershipError =
			groupMembership && !groupMembership.isProcessing && !groupMembership.created && groupMembership.error;
		if (hasGroupMembershipError) {
			logger.error(hasGroupMembershipError);
		}
		const isLoading = auth.isProcessing || isFetching || (groupMembership && groupMembership.isProcessing);

		const groupAvatar =
			group && group.avatarUrlLarge
				? group.avatarUrlLarge.startsWith('//')
					? `https:${group.avatarUrlLarge}`
					: group.avatarUrlLarge
				: '';
		const meta = createOpenGraphMetadata(groupAvatar);

		return (
			<div className="group-page">
				<Helmet title={group && group.name ? group.name : ''} meta={meta} />
				{isLoading ? (
					<LoadingIndicator />
				) : !group ? (
					<NotFound />
				) : (
					<div className="content">
						<div className="group-info">
							{group.avatarUrlLarge && (
								<img className="group-info-avatar" alt={group.name} src={group.avatarUrlLarge} />
							)}
							<div className="group-info-text">
								<div>
									<a href={`${baseUrls.faithlife}/${groupToken}`}>{group.name}</a>
								</div>
								<div className="b2">{groupInfoParts.join(' \u2022 ')}</div>
							</div>
							{showCreateGroupMembership && !purchaseInfo && (
								<div className="group-info-membership-action">
									{hasGroupMembershipError ? (
										<div className="b2" style={{ marginBottom: 10 }}>
											Error Creating Group Membership
										</div>
									) : null}
									{groupMembershipAction === 'join' && !hasGroupMembershipError ? (
										<button onClick={() => this._createGroupMembership(group.groupId, groupMembershipAction)}>
											Join on Faithlife
										</button>
									) : groupMembershipAction === 'follow' && !hasGroupMembershipError ? (
										<button onClick={() => this._createGroupMembership(group.groupId, groupMembershipAction)}>
											Follow on Faithlife
										</button>
									) : null}
								</div>
							)}
						</div>
						{(group.items && group.items.length) || (group.subcategories && group.subcategories.length) ? (
							<CompactItemList
								items={group.items}
								categories={group.subcategories}
								imageHeight={170}
								subscription={subscription}
							/>
						) : (
							<div className="no-items">
								<span>No Items</span>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}
